
import ProductBase from "@/common/ProductBase.vue"

// Asked once per page, not per row: a category of 8 accessories would otherwise
// run the same media query 8 times, and the answer cannot change mid-session.
let hoverCapable = null

function canHover() {
  if (hoverCapable === null) {
    hoverCapable = typeof window !== "undefined" && !!window.matchMedia?.("(hover: hover)").matches
  }

  return hoverCapable
}

export default {
  extends: ProductBase,

  data() {
    return {
      // One-shot: the link's quantity is reconciled against the cart on arrival
      // and never again, so it cannot fight the dealer editing the stepper after.
      prefillApplied: false
    }
  },

  props: {
    product: {
      type: Object,
      required: true
    },

    // Owned by the page, not by this row: a row inside a collapsed accessory
    // category can be torn down and rebuilt, and local state would come back
    // "Add" for something already in the request — letting it be added twice.
    isAdded: {
      type: Boolean,
      default: false
    },

    // Quantity the external configurator asked for in the link, 0 if it didn't
    // name this accessory. Only seeds the stepper — nothing is put in the cart
    // on the dealer's behalf.
    prefilledCount: {
      type: Number,
      default: 0
    }
  },

  computed: {
    // This accessory's existing line in the dealer's cart, if it has one.
    //
    // The cart is the source of truth rather than a local "added" flag: a row in
    // a collapsed category gets torn down and rebuilt, and local state would
    // come back as "add" for something already in the cart — which is how the
    // category's "N added" badge (page-level, cart-derived) could disagree with
    // the row beneath it. CART/ADD and CART/REMOVE both commit the new cart, so
    // this follows along on its own.
    getCartLine() {
      if (!this.__isLoggedIn) return null

      return (this.CART?.items || []).find((item) => item.id === this.product.id) || null
    },

    getIsInCart() {
      return !!this.getCartLine
    },

    // What the stepper shows: the quantity already in the cart if there is a
    // line, otherwise the one the configurator's link asked for, otherwise one.
    // The cart wins — it is what the dealer will actually be charged for.
    getCartQuantity() {
      return this.getCartLine?.quantity?.current ?? (this.prefilledCount || 1)
    },

    // The row shows the offer's name, so the image alt and the link label say the
    // same thing rather than the parent product's name.
    getName() {
      return this.offer.name || this.product.name
    },

    // Hover preview. The thumbnail is 48-64px, too small to tell one sauna bucket
    // from another, and a CSS scale would be clipped by the category panel's own
    // scroll box — the tooltip is portaled to <body>, so it escapes it. Skipped
    // where there is no hover, so a tap on a phone just opens the product.
    getPhotoPreview() {
      const photo = this.offer.photo || this.product.photo

      if (!photo || !canHover()) return { content: null }

      // Only the characters that would break out of the attribute — encodeURI
      // would double-escape the %20s already in these S3 keys.
      const src = String(photo).replace(/"/g, "%22").replace(/</g, "%3C")

      return {
        content: `<img class="accessory__preview" src="${src}" alt="" />`,
        placement: "left",
        offset: 8,
        delay: { show: 150, hide: 0 }
      }
    },

    getPrice() {
      if (typeof this.offer.price === "string" && this.offer.price?.includes("-")) {
        return this.offer.price
      } else {
        return this.offer.price * this.quantityCurrent
      }
    },

    getMSRP() {
      if (typeof this.offer.msrpPrice === "string" && this.offer.msrpPrice?.includes("-")) {
        return this.offer.msrpPrice
      } else {
        return this.offer.msrpPrice * this.quantityCurrent
      }
    }
  },

  watch: {
    // The prices on this row are per-unit × quantityCurrent, so an accessory
    // already in the cart has to price against the quantity that is in it —
    // otherwise a line ordered three times reads as the cost of one. immediate,
    // because the cart can resolve after this row is built.
    getCartQuantity: {
      immediate: true,
      handler(quantity) {
        this.quantityCurrent = quantity
      }
    },

    // The dealer asked for a quantity in the configurator and this accessory is
    // already on an older cart line at a different one. The configurator is the
    // newer statement of what they want, so it wins — and it is the same line,
    // not a new one, so nothing appears in their cart that they did not put there.
    //
    // Keyed on the cart having loaded rather than on the line: a row whose
    // accessory is NOT in the cart never sees its line change, so watching the
    // line would leave the decision pending and then fire on a later manual add,
    // overwriting a quantity the dealer had just chosen by hand.
    // Not `immediate`: Vue initialises watchers before it calls created(), and
    // ProductBase resolves `offer` in created() — so an immediate handler would
    // run against a null offer whenever the cart had already loaded, and the
    // add path reads offer.count through getQuantityValid. mounted() covers the
    // cart-already-there case instead, by which point the offer exists.
    "CART.loaded"(loaded) {
      if (loaded) this.applyPrefilledQuantity()
    }
  },

  mounted() {
    if (this.CART?.loaded) this.applyPrefilledQuantity()
  },

  methods: {
    toggle() {
      if (this.isAdded) {
        this.removeFromRequest()
      } else {
        this.addToRequest()
      }
    },

    addToRequest() {
      this.$emit("addToRequest", {
        id: this.product.id,
        count: this.quantityCurrent
      })
    },

    removeFromRequest() {
      this.$emit("removeFromRequest", { id: this.product.id })
    },

    onQuantityChange(quantity) {
      this.quantityCurrent = quantity

      // Already in the request: re-send it so the page holds the quantity now on
      // screen, rather than whichever one was showing when Add was clicked.
      // Guests only — a dealer's row is backed by a real cart line instead.
      if (!this.__isLoggedIn && this.isAdded) {
        this.addToRequest()
      }
    },

    // Fires on +/- and on enter/blur, not on every keystroke — so an already
    // ordered accessory writes its new quantity back to the cart once the dealer
    // settles on it, rather than the field quietly disagreeing with the order.
    async onQuantityCommit(quantity) {
      if (!this.getIsInCart || quantity === this.getCartQuantity) return

      await this.changeCartQuantity(quantity)
    },

    // Runs at most once per arrival — the guard is set as soon as the cart is
    // known, so nothing here can fire again against a later manual change.
    //
    // Every accessory the link names goes into the cart at the quantity it asked
    // for. Whether that means changing an existing line or creating one is an
    // accident of what was in the cart already — one path, no special cases.
    // Each add toasts, so the cart never changes without the shopper being told.
    async applyPrefilledQuantity() {
      if (this.prefillApplied || !this.prefilledCount) return

      this.prefillApplied = true
      this.quantityCurrent = this.prefilledCount

      // A customer arrives by the same link and has no cart to put anything in —
      // the backend refuses PATCH /api/cart for a guest and does not persist a
      // guest add. Their accessories belong to the price request they are about
      // to fill in on this page, which is where the rest of their build goes too.
      if (!this.__isLoggedIn) {
        this.addToRequest()
        return
      }

      const line = this.getCartLine

      if (!line) {
        // The same conditions the Add button is disabled under. Without them an
        // accessory whose variant hasn't resolved would be ordered under the
        // placeholder offer ProductBase.setDefaultOffer builds, i.e. the wrong id.
        if (!this.getAllSpecsSelected || !this.getHasActiveOffers || !this.getQuantityValid) return

        await this.addToCart()
        return
      }

      if (Number(line.quantity?.current) === this.prefilledCount) return

      await this.changeCartQuantity(this.prefilledCount)
    },

    async changeCartQuantity(quantity) {
      this.isAddingToCart = true

      try {
        await this.$store.dispatch("CART/CHANGE", {
          id: this.product.id,
          objectId: this.getCartLine.objectId,
          quantity
        })
      } catch (err) {
        this.__logError(err)
        this.__toastError(err?.source === "server" ? err.message : "")
      }

      this.isAddingToCart = false
    }
  }
}
